import { initRenderer } from "@infinitaslearning/module-player2";
import { getAnalyticsTracker, setAnalyticsPage } from "analytics";
import { Breadcrumbs } from "components/Breadcrumbs/Breadcrumbs";
import { Icon } from "components/Icon/Icon";
import { Player } from "components/Player/Player";
import { ProgressBar } from "components/ProgressBar/ProgressBar";
import { useBook } from "hooks/useBook";
import { useTest } from "hooks/useTest";
import { observer } from "mobx-react";
import type { PathParams } from "models/path/Path";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  BackButton,
  StyleBreadcrumbsContainer,
  StyleButtonText,
  StyleContainer,
  StyleHeading4,
  StyleProgressContainer,
  StyleTestContainer,
  StyleTestIntro,
  TitleContainer,
} from "./Test.styled";

const ContentToHtml = initRenderer();

export const Test: React.FC = observer(() => {
  const { fetchChapterTest, chapterTestInfo, loading } = useTest();
  const [renderPlayer, setRenderPlayer] = useState(!loading);
  const [answered, setAnswered] = useState(0);
  const [initialData, setInitialData] = useState({
    topics: chapterTestInfo
      .filter((c) => c.question?.portableTextContent)
      .map((elem) => [elem.question]),
    interactions: [[]],
  });
  const { bookInfo, fetchBook } = useBook();
  const { additionalsId, chapterId }: PathParams = useParams();

  useEffect(() => {
    if (getAnalyticsTracker()) {
      setAnalyticsPage(`${additionalsId}${chapterId}`, "exam", additionalsId);
    }
  }, [additionalsId, chapterId]);

  useEffect(() => {
    fetchBook(additionalsId);
  }, [fetchBook, additionalsId]);

  const additionalsPath = `/${additionalsId}`;

  useEffect(() => {
    fetchChapterTest(chapterId, additionalsId);
  }, [fetchChapterTest, additionalsId, chapterId]);

  const { t } = useTranslation("test");

  useEffect(() => {
    const data = {
      topics: chapterTestInfo
        .filter((c) => c.question?.portableTextContent)
        .map((ch) => [ch.question]),
      interactions: [[]],
    };

    setInitialData(data);
    setRenderPlayer(!loading);
  }, [chapterTestInfo, loading]);

  const chapterTitle = bookInfo.chapters.find((ch) => ch.id === chapterId)?.title;

  const testTitle = (
    <TitleContainer>
      <Icon className="icon-test" />
      <StyleHeading4>{chapterTitle}</StyleHeading4>
    </TitleContainer>
  );

  const backToCourse = (
    <BackButton to={additionalsPath}>
      <Icon className="icon-arrow_left" />
      <StyleButtonText>{t("backButton", "Back to course")}</StyleButtonText>
    </BackButton>
  );

  const breadCrumbsProps = {
    pathnames: [additionalsPath, additionalsPath],
    textValues: [testTitle, bookInfo?.title],
  };

  return (
    <StyleContainer>
      <StyleBreadcrumbsContainer>
        {backToCourse}
        <Breadcrumbs {...breadCrumbsProps} />
        <div />
      </StyleBreadcrumbsContainer>
      <StyleTestContainer>
        <StyleTestIntro>
          {chapterTestInfo.length > 0 &&
            chapterTestInfo[answered] &&
            chapterTestInfo[answered].intro &&
            chapterTestInfo[answered].intro?.portableTextContent && (
              <ContentToHtml content={chapterTestInfo[answered].intro.portableTextContent} />
            )}
        </StyleTestIntro>
        {chapterTestInfo.length > 0 && initialData.topics.length > 0 && renderPlayer && (
          <Player initialData={initialData} questionAnswered={() => setAnswered(answered + 1)} />
        )}
      </StyleTestContainer>
      <StyleProgressContainer>
        <ProgressBar progress={answered} total={chapterTestInfo.length} />
      </StyleProgressContainer>
    </StyleContainer>
  );
});
